import { useEffect, useMemo, useRef, useState } from "react"
import nProgress from "nprogress"

/**
 * 270726 (permintaan user): picker produk MULTI-SELECT utk retur manual — pola sama
 * dengan form pembelian (pages/purchase/product-picker-modal.jsx). Sebelumnya item
 * retur hanya bisa ditambah satu-satu lewat dropdown. Search sekali → centang banyak
 * unit (pilihan bertahan lintas halaman/pencarian) → isi qty → tambah sekaligus.
 *
 * Sumber data = endpoint yang SAMA dengan dropdown lama (/product-unit-stocks,
 * aggregate + only_in_stock) supaya stok & harga baris identik.
 *
 * Qty diketik dalam SATUAN GLOBAL retur (`uom`/`factor`); onAdd mengembalikan qty
 * yang sudah dikonversi ke PCS (state internal form retur selalu PCS).
 */
export default function ReturnProductPickerModal({
  open,
  onClose,
  onAdd,
  outletId,
  uom = "PCS",
  factor = 1,
}) {
  const searchInput = useRef(null)
  const debounceRef = useRef(null)

  const [searchValue, setSearchValue] = useState("")
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  // Map pid -> { product, qty } — qty dalam satuan global retur; bertahan saat
  // ganti halaman / kata kunci.
  const [selected, setSelected] = useState(() => new Map())

  useEffect(() => {
    if (!open) return
    setSearchValue("")
    setSearch("")
    setPage(1)
    setSelected(new Map())
    const t = setTimeout(() => searchInput.current?.focus(), 50)
    return () => clearTimeout(t)
  }, [open])

  useEffect(() => () => clearTimeout(debounceRef.current), [])

  useEffect(() => {
    if (!open || !outletId) {
      setRows([])
      setMeta({})
      return
    }

    const ctrl = new AbortController()
    const run = async () => {
      try {
        setIsLoading(true)
        nProgress.start()

        const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
        const params = new URLSearchParams()
        params.set("outlet_id", String(outletId?.id ?? outletId))
        params.set("only_in_stock", "1")
        params.set("aggregate", "1")
        params.set("paginate", "true")
        params.set("page", String(page))
        params.set("limit", "15")
        if (search) params.set("search", search)

        const res = await fetch(`${base}/product-unit-stocks?${params.toString()}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          signal: ctrl.signal,
        })
        const json = await res.json().catch(() => ({}))
        const payload = json?.data || json

        if (Array.isArray(payload?.data)) {
          setRows(payload.data)
          setMeta(payload)
        } else if (Array.isArray(payload)) {
          setRows(payload)
          setMeta({})
        } else {
          setRows([])
          setMeta({})
        }
      } catch (e) {
        if (e?.name !== "AbortError") {
          setRows([])
          setMeta({})
        }
      } finally {
        setIsLoading(false)
        nProgress.done()
      }
    }

    run()
    return () => ctrl.abort()
  }, [open, outletId, page, search])

  const pidOf = (p) => String(p?.product_unit_id ?? p?.id ?? "")
  const labelOf = (p) =>
    p?.product_unit?.long_name ||
    p?.product_unit?.name ||
    p?.product_unit_long_name ||
    p?.long_name ||
    p?.product_unit_name ||
    p?.name ||
    pidOf(p) ||
    "-"
  const stockOf = (p) =>
    Number(p?.current_stock ?? p?.available_stock ?? p?.stock ?? p?.qty_on_hand ?? 0) || 0
  const descOf = (p) =>
    [
      p?.product_model,
      p?.sku ? `SKU: ${p.sku}` : null,
      `Stok: ${stockOf(p).toLocaleString("id-ID")} pcs`,
    ]
      .filter(Boolean)
      .join(" • ")

  // Maksimum qty dalam satuan global (stok pcs ÷ faktor), dibulatkan ke bawah supaya
  // tidak pernah melebihi stok saat dikonversi balik ke pcs.
  const maxInUom = (p) => Math.floor(stockOf(p) / (factor || 1))

  const toggle = (p) => {
    const pid = pidOf(p)
    if (!pid) return
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(pid)) next.delete(pid)
      else next.set(pid, { product: p, qty: 1 })
      return next
    })
  }

  const setQty = (pid, qty) => {
    setSelected((prev) => {
      const next = new Map(prev)
      const cur = next.get(pid)
      if (cur) next.set(pid, { ...cur, qty })
      return next
    })
  }

  const selectableRows = useMemo(() => rows.filter((p) => stockOf(p) > 0), [rows])
  const allPageSelected =
    selectableRows.length > 0 && selectableRows.every((p) => selected.has(pidOf(p)))

  const togglePage = () => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (allPageSelected) {
        for (const p of selectableRows) next.delete(pidOf(p))
      } else {
        for (const p of selectableRows) {
          const pid = pidOf(p)
          if (pid && !next.has(pid)) next.set(pid, { product: p, qty: 1 })
        }
      }
      return next
    })
  }

  const handleAdd = () => {
    const entries = [...selected.values()]
      .map((x) => {
        const stock = stockOf(x.product)
        const qtyUom = Number(x.qty) > 0 ? Number(x.qty) : 1
        // Konversi ke PCS (state internal form retur) + clamp ke stok.
        const qtyPcs = Math.max(1, Math.min(Math.round(qtyUom * (factor || 1)), stock))
        return { product: x.product, qty_pcs: qtyPcs }
      })
      .filter((x) => x.qty_pcs > 0)

    if (!entries.length) return
    onAdd?.(entries)
  }

  if (!open) return null

  const hasNext = Boolean(meta?.next_page_url)
  const hasPrev = Boolean(meta?.prev_page_url)

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Pilih Produk Retur</h3>
            <p className="text-sm text-neutral-500">
              Centang beberapa produk sekaligus — pilihan tetap tersimpan saat ganti halaman
              atau kata kunci. Qty diisi dalam satuan <b>{uom}</b>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-4">
          <input
            ref={searchInput}
            value={searchValue}
            onChange={(e) => {
              const val = e.target.value
              setSearchValue(val)
              clearTimeout(debounceRef.current)
              debounceRef.current = setTimeout(() => {
                setSearch(String(val || "").trim())
                setPage(1)
              }, 400)
            }}
            type="text"
            placeholder="Cari nama produk / SKU..."
            className="w-full py-3 pl-4 pr-10 text-sm border rounded-xl border-neutral-200 focus:outline-none focus:ring"
          />

          <div className="border rounded-2xl overflow-hidden">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 text-xs font-semibold border-b bg-neutral-50 text-neutral-600">
              <div className="col-span-1">
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={togglePage}
                  title="Pilih semua di halaman ini"
                />
              </div>
              <div className="col-span-7">Produk</div>
              <div className="col-span-2">Satuan</div>
              <div className="col-span-2">Qty ({uom})</div>
            </div>

            <div className="max-h-[420px] overflow-y-auto">
              {isLoading ? (
                <div className="px-4 py-10 text-sm text-center text-neutral-500">
                  Memuat produk...
                </div>
              ) : !rows.length ? (
                <div className="px-4 py-10 text-sm text-center text-neutral-500">
                  Produk tidak ditemukan.
                </div>
              ) : (
                rows.map((p) => {
                  const pid = pidOf(p)
                  const picked = selected.get(pid)
                  const stock = stockOf(p)
                  const max = maxInUom(p)

                  return (
                    <div
                      key={pid}
                      className={`grid grid-cols-12 gap-3 items-center px-4 py-3 text-sm border-b last:border-b-0 ${
                        stock <= 0
                          ? "opacity-50 cursor-not-allowed"
                          : picked
                          ? "bg-orange-50 cursor-pointer"
                          : "hover:bg-neutral-50 cursor-pointer"
                      }`}
                      onClick={() => stock > 0 && toggle(p)}
                    >
                      <div className="col-span-1">
                        <input
                          type="checkbox"
                          checked={Boolean(picked)}
                          disabled={stock <= 0}
                          readOnly
                        />
                      </div>
                      <div className="col-span-7 min-w-0">
                        <div className="font-medium text-neutral-900 truncate">{labelOf(p)}</div>
                        <div className="text-xs text-neutral-500 truncate">{descOf(p)}</div>
                      </div>
                      <div className="col-span-2 text-xs text-neutral-600">
                        {uom}
                        {(factor || 1) > 1 ? (
                          <div className="text-[11px] text-neutral-400">Max {max} {uom}</div>
                        ) : null}
                      </div>
                      <div className="col-span-2" onClick={(e) => e.stopPropagation()}>
                        {picked ? (
                          <input
                            type="number"
                            min={1}
                            step="any"
                            value={picked.qty}
                            onChange={(e) => setQty(pid, e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border rounded-lg border-neutral-200 focus:outline-none focus:ring"
                          />
                        ) : (
                          <span className="text-xs text-neutral-300">—</span>
                        )}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-neutral-600">
              <button
                type="button"
                disabled={!hasPrev}
                onClick={() => setPage((x) => Math.max(1, x - 1))}
                className="px-3 py-1.5 border rounded-lg border-neutral-200 disabled:opacity-40"
              >
                ‹ Prev
              </button>
              <span>Hal {page}</span>
              <button
                type="button"
                disabled={!hasNext}
                onClick={() => setPage((x) => x + 1)}
                className="px-3 py-1.5 border rounded-lg border-neutral-200 disabled:opacity-40"
              >
                Next ›
              </button>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-xs text-neutral-600">
                <b>{selected.size}</b> produk dipilih
              </span>
              {selected.size > 0 && (
                <button
                  type="button"
                  onClick={() => setSelected(new Map())}
                  className="text-xs text-neutral-500 underline"
                >
                  Bersihkan
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-xs border rounded-xl border-neutral-200 text-neutral-700"
              >
                Batal
              </button>
              <button
                type="button"
                disabled={!selected.size}
                onClick={handleAdd}
                className={`px-4 py-2.5 text-xs font-semibold text-white rounded-xl ${
                  selected.size
                    ? "bg-brand-900 hover:bg-brand-800"
                    : "bg-neutral-400 cursor-not-allowed"
                }`}
              >
                Tambah {selected.size || ""} Produk
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
